import {Alert, Anchor, Button, Group, Stack, Text} from '@mantine/core'
import {useEffect, useState} from 'react'

import {CustomerPocSelector} from '../customers/customer-poc-selector'
import {useEditRights} from '../jobs/edit-rights-context'
import type {ProjectDetail} from './schemas'
import {usePatchProject} from './use-projects'

type ProjectCustomerPocSectionProps = {
    project: ProjectDetail
}

export function ProjectCustomerPocSection({project}: ProjectCustomerPocSectionProps) {
    const {canEdit} = useEditRights()
    const patchProject = usePatchProject(project.id)
    const assignedContactId = project.customerContact?.id ?? null
    const [isEditing, setIsEditing] = useState(false)
    const [selectedContactId, setSelectedContactId] = useState<string | null>(assignedContactId)
    const [saveError, setSaveError] = useState<string | null>(null)

    useEffect(() => {
        setSelectedContactId(assignedContactId)
    }, [project.id, assignedContactId])

    async function handleSave() {
        setSaveError(null)
        try {
            await patchProject.mutateAsync({
                name: project.name,
                description: project.description,
                notes: project.notes,
                customerId: project.customerId,
                internalContactId: project.internalContact?.id ?? null,
                customerContactId: selectedContactId,
            })
            setIsEditing(false)
        } catch (saveFailure) {
            setSaveError(
                saveFailure instanceof Error
                    ? saveFailure.message
                    : 'Failed to update customer point of contact',
            )
        }
    }

    function handleCancel() {
        setSelectedContactId(assignedContactId)
        setSaveError(null)
        setIsEditing(false)
    }

    return (
        <Stack gap="md">
            {isEditing ? (
                <Text size="sm" fw={600}>
                    Customer point of contact
                </Text>
            ) : null}

            {isEditing ? (
                <Stack gap="sm">
                    <CustomerPocSelector
                        customerId={project.customerId}
                        value={selectedContactId}
                        onChange={setSelectedContactId}
                    />
                    {saveError ? (
                        <Alert color="red" title="Could not update customer point of contact">
                            {saveError}
                        </Alert>
                    ) : null}
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            size="compact-sm"
                            onClick={handleCancel}
                            disabled={patchProject.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="compact-sm"
                            onClick={() => void handleSave()}
                            loading={patchProject.isPending}
                        >
                            Save
                        </Button>
                    </Group>
                </Stack>
            ) : project.customerContact ? (
                <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                    p="xs"
                    style={{
                        background: 'var(--mantine-color-gray-0)',
                        border: '1px solid var(--mantine-color-gray-2)',
                        borderRadius: 10,
                    }}
                >
                    <Stack gap={2} style={{flex: 1, minWidth: 0}}>
                        <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            tt="uppercase"
                            style={{letterSpacing: '0.04em'}}
                        >
                            Customer POC
                        </Text>
                        <Text size="sm" fw={700}>
                            {project.customerContact.name}
                        </Text>
                        {project.customerContact.title ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Title:
                                </Text>
                                <Text size="sm">{project.customerContact.title}</Text>
                            </Group>
                        ) : null}
                        {project.customerContact.phone ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Phone:
                                </Text>
                                <Text size="sm">{project.customerContact.phone}</Text>
                            </Group>
                        ) : null}
                        {project.customerContact.email ? (
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    Email:
                                </Text>
                                <Anchor href={`mailto:${project.customerContact.email}`} size="sm">
                                    {project.customerContact.email}
                                </Anchor>
                            </Group>
                        ) : null}
                    </Stack>
                    {canEdit ? (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setIsEditing(true)}
                        >
                            Reassign
                        </Button>
                    ) : null}
                </Group>
            ) : (
                <Group
                    justify="space-between"
                    align="center"
                    wrap="nowrap"
                    p="xs"
                    style={{border: '1px dashed var(--mantine-color-gray-4)', borderRadius: 10}}
                >
                    <Stack gap={0} style={{flex: 1, minWidth: 0}}>
                        <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            tt="uppercase"
                            style={{letterSpacing: '0.04em'}}
                        >
                            Customer POC
                        </Text>
                        <Text size="sm" c="dimmed">
                            Not assigned
                        </Text>
                    </Stack>
                    {canEdit ? (
                        <Button size="compact-sm" onClick={() => setIsEditing(true)}>
                            Assign
                        </Button>
                    ) : null}
                </Group>
            )}
        </Stack>
    )
}
